import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DeviceCatalogEntry } from '@mvcp/contracts';
import { getDevices, updateCuratedDeviceVersion } from '../api';
import { ConfirmModal } from './ConfirmModal';
import { deviceLabel, deviceRevision } from './device-label';

import { Logo } from './Logo';
const fieldStyle: React.CSSProperties = {
  padding: '8px 10px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--rounded-md)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
};

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ready'; entries: DeviceCatalogEntry[] }
  | { kind: 'error'; message: string };

/**
 * Admin Console → Devices (Story 8.5, AD-25 / AD-6). The curated Device catalog with one
 * edit affordance per row: record or clear a device's hardware/firmware revision.
 *
 * **Why this is its own view rather than a reuse.** `DeviceDirectory` looks like the
 * obvious host and is the wrong one twice over: it is mounted for a Compliance Manager
 * (`CockpitShell`), and it reads `getDeviceDirectory()` — the devices already INSTALLED
 * somewhere on the caller's fleet. A curated device nobody has installed, which is
 * exactly the version-less population this view exists for (the seven `009_device.sql`
 * rows), would be invisible there. `GET /api/v1/devices` already admits a system
 * administrator and already returns `version`, so the read half needed nothing new.
 *
 * **Why the revision is the only editable field.** `model` is IDENTITY (AD-24 clause 1);
 * `category` is out of scope; and a tenant PROPOSAL is not editable here at all — the
 * list is filtered to curated rows and the server 409s a proposal id, because enriching a
 * proposal before promotion is the Requests inbox's "Edit before verifying" form (Story
 * 8.12), which edits the model too. There is deliberately no CREATE path:
 * Story 2.6's admin add-curated-device arm stays open, and `device_update` and
 * `device_propose` are different policies with different actors.
 *
 * Follows `UsersView`'s edit shape: a `ConfirmModal` with an `aria-label`led input,
 * `confirmLabel: 'Save'`, an explicit "This action is logged.", nothing sent when nothing
 * changed, and a failure kept INSIDE the still-open dialog (the list behind the scrim is
 * not visible).
 */
export function CuratedDevicesView(): React.JSX.Element {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [notice, setNotice] = useState<string | null>(null);
  /** The row being edited, plus the draft revision (empty string = cleared). */
  const [editing, setEditing] = useState<{ entry: DeviceCatalogEntry; draft: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  /**
   * Story 8.5 — the dialog's ADVISORY slot, distinct from `ConfirmModal`'s `error`.
   *
   * Two of this form's messages are not errors at all: "No changes to save." states a fact
   * about the draft, and the unsaved-changes prompt is a deliberate confirmation. Routing
   * either through `ConfirmModal`'s `role="alert"` `aria-live="assertive"` region would
   * interrupt a screen reader for something the administrator has not got wrong — the exact
   * distinction the propose form's advisory is built on (an error is the result of an
   * action and must interrupt; an advisory appears while the administrator is still in the
   * form). So they render inside the dialog body as their own `role="status"`
   * `aria-live="polite"` element with `data-tone="warn"`, and the assertive region stays
   * reserved for a genuine server failure.
   */
  const [editAdvisory, setEditAdvisory] = useState<string | null>(null);
  /**
   * Story 8.5 — a dismissal that would throw away a typed revision is WARNED, not
   * silent. `ConfirmModal` is a confirmation shell, not a form shell: Esc and a scrim
   * click both route to `onCancel`, so an off-panel click or a stray key would otherwise
   * discard the administrator's input with no trace. When the draft differs from what is
   * stored, the first dismissal only says so; a second one discards. Any further typing
   * re-arms the warning, so it can never be spent in advance.
   */
  const [discardWarned, setDiscardWarned] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      setState({ kind: 'ready', entries: await getDevices() });
    } catch (err: unknown) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Failed to load the device catalog',
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Curated rows only: a tenant proposal belongs in the Requests inbox (Verify / Merge),
   * and this endpoint refuses one anyway. Ordered by vendor then label so two revisions
   * of one model sit together.
   */
  const curated = useMemo(() => {
    if (state.kind !== 'ready') return [];
    return state.entries
      .filter((e) => !e.device.proposedByCompanyId)
      .sort(
        (a, b) =>
          a.vendor.name.localeCompare(b.vendor.name) ||
          deviceLabel(a.device).localeCompare(deviceLabel(b.device)),
      );
  }, [state]);

  async function onSave(): Promise<void> {
    if (!editing) return;
    const trimmed = editing.draft.trim();
    // BOTH sides trimmed. The stored value is compared trimmed because the label owner reads
    // it that way (`deviceRevision`: `' r3 '` and `'r3'` are one revision), and because the
    // input is prefilled with the RAW stored value: against an untrimmed `current`, opening a
    // padded legacy row and pressing Save with nothing typed fell through this no-op branch,
    // missed the server's own short-circuit too, and wrote + audited a change the
    // administrator never made. See the matching trim in `onCancel`'s `dirty` test.
    const current = (editing.entry.device.version ?? '').trim();
    // The value this Save would WRITE: emptying the field is an explicit clear (`null`),
    // anything else is the trimmed revision.
    const intended = editing.draft === '' ? null : trimmed;
    // …compared against the STORED BYTES when the intent is a clear, and against the trimmed
    // stored value otherwise.
    //
    // Both halves matter. Comparing trimmed is what fixed the padded-legacy row (opening
    // `'  r3  '` and pressing Save with nothing typed must be a no-op, not a phantom write).
    // But comparing ONLY trimmed made a WHITESPACE-ONLY stored revision unclearable: `'   '`
    // trims to `''`, so emptying the field entirely also compared equal and this branch answered
    // "No changes to save." — even though the server compares raw and WOULD have cleared it
    // (`admin-device.repository.ts`: `'   ' !== null`). That row then had a revision the label
    // owner correctly refuses to show, a Revision cell rendering invisible whitespace, and no
    // way for any administrator to remove it — on the one repair path AD-25 clause 5 promises
    // "clearing is possible and explicit". No current writer can produce that state
    // (`parseDeviceVersion` rejects blanks, the importer's `optionalTrimmed` returns
    // `undefined`), which is why it went unnoticed; this story treats the state as real in three
    // other places, so the one path that repairs devices must too.
    const noChange = intended === null ? (editing.entry.device.version ?? null) === null : trimmed === current;
    // Nothing changed → no request (no phantom audit row, no spurious 400), just say so.
    // Mirrors `UsersView`'s "No changes to save." — but as an ADVISORY, not an error: the
    // administrator did nothing wrong, so it must not interrupt.
    if (noChange) {
      setEditError(null);
      setEditAdvisory('No changes to save.');
      return;
    }
    // A field holding only whitespace is a TYPO, not a clear. Sending `null` here would
    // route around the server's blank→400 rule (AR-19: nothing is silently changed) and
    // wipe a shared-backbone revision every Company reads, off a stray space. Clearing
    // stays possible and stays explicit: empty the field completely.
    if (editing.draft !== '' && trimmed === '') {
      setEditError(null);
      setEditAdvisory(
        'A blank is not a clear. To remove this revision, empty the field completely; otherwise type the revision to record.',
      );
      return;
    }
    setBusy(true);
    setEditError(null);
    setEditAdvisory(null);
    try {
      // A cleared field is an EXPLICIT null — the server refuses a blank string rather
      // than treating it as a clear, so the intent has to be stated on the wire.
      await updateCuratedDeviceVersion(editing.entry.device.id, trimmed === '' ? null : trimmed);
    } catch (err: unknown) {
      // Keep the failure inside the open dialog (ConfirmModal contract).
      setEditError(err instanceof Error ? err.message : "Couldn't save. Try again.");
      setBusy(false);
      return;
    }
    setBusy(false);
    // Named through the label OWNER, for the same reason the Edit control is (see its
    // `aria-label`): two revisions of one model sit adjacent in this table, so a bare-model
    // acknowledgement cannot say which of the two rows was just changed — on the one surface
    // built to tell them apart. The label is the row as it read BEFORE the edit, which is how
    // the administrator identified it.
    const named = deviceLabel(editing.entry.device);
    setNotice(
      trimmed === ''
        ? `Cleared the revision on ${named}.`
        : `Recorded revision ${trimmed} on ${named}.`,
    );
    setEditing(null);
    setDiscardWarned(false);
    setEditAdvisory(null);
    await load();
  }

  return (
    <>
      <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>Devices</h1>
      <p className="muted" style={{ margin: 0 }}>
        The curated Device catalog every Company shares. Record or correct a device&rsquo;s
        hardware/firmware revision — the revision is part of how the device is named
        wherever a certificate&rsquo;s subject is shown, so a device that has one should
        carry it. Tenant-proposed devices are curated under Requests, not here.
      </p>

      <div role="status" aria-live="polite" style={{ minHeight: 16 }}>
        {notice && (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            {notice}
          </p>
        )}
      </div>

      <section className="card stack" aria-labelledby="curated-devices-heading">
        <h2 id="curated-devices-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
          Curated devices
        </h2>

        {state.kind === 'loading' && (
          <p className="muted" style={{ margin: 0 }}>
            Loading…
          </p>
        )}

        {state.kind === 'error' && (
          <div className="stack" style={{ gap: 8 }} role="alert">
            <p className="muted" style={{ margin: 0 }}>
              {state.message}
            </p>
            <button
              type="button"
              className="btn-accent"
              data-surface="accent"
              onClick={() => void load()}
              style={{ alignSelf: 'flex-start' }}
            >
              Retry
            </button>
          </div>
        )}

        {state.kind === 'ready' && curated.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>
            No curated devices in the catalog.
          </p>
        )}

        {state.kind === 'ready' && curated.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <div className="table-scroll">
              <table className="data-table">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Model</th>
                  <th>Revision</th>
                  <th data-align="right" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {curated.map(({ device, vendor }) => (
                  <tr key={device.id}>
                    <td>
                    <span className="cell-identity">
                      <Logo id={vendor.id} name={vendor.name} logo={vendor.logo} />
                      {vendor.name}
                    </span>
                  </td>
                    <td>{device.model}</td>
                    <td>
                      {/* Its own cell, not a concatenation — the subject LABEL is composed
                          by `device-label.ts` on the certificate surfaces. */}
                      {/* `deviceRevision`, not raw truthiness: a whitespace-only stored value is
                          truthy but is NOT a revision, and rendering it produced a cell of
                          invisible whitespace where the em dash means "none recorded". Same
                          correction the advisory's `describeMatch` needed — the owner decides
                          what counts as a revision and every surface follows it. */}
                      {deviceRevision(device) ? <span data-numeric>{deviceRevision(device)}</span> : '—'}
                    </td>
                    <td data-align="right">
                      <button
                        type="button"
                        className="icon-button"
                        onClick={() => {
                          setEditError(null);
                          setEditAdvisory(null);
                          setNotice(null);
                          setDiscardWarned(false);
                          setEditing({ entry: { device, vendor }, draft: device.version ?? '' });
                        }}
                        // The LABEL, not the bare model: this list deliberately sorts two
                        // revisions of one model together, so a bare-model name would give
                        // them two identical Edit buttons on the one surface built to tell
                        // them apart.
                        aria-label={`Edit revision for ${deviceLabel(device)}`}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {editing && (
        <ConfirmModal
          title="Edit revision"
          confirmLabel="Save"
          busy={busy}
          error={editError}
          message={
            <div className="stack" style={{ gap: 8 }}>
              <p style={{ margin: 0 }}>
                Record the hardware/firmware revision for{' '}
                <strong>{deviceLabel(editing.entry.device)}</strong> by{' '}
                <strong>{editing.entry.vendor.name}</strong>. Every Company shares this
                record, so it will change how the device is named on their certificate
                surfaces too. The model is not editable — it is the device&rsquo;s identity.
                Leave the field empty to clear the revision. This action is logged.
              </p>
              <label className="stack" style={{ gap: 4 }}>
                <span className="field-label">Revision</span>
                <input
                  aria-label="Device revision"
                  placeholder="e.g. v7.2 — leave empty to clear"
                  value={editing.draft}
                  onChange={(e) => {
                    setEditing({ ...editing, draft: e.target.value });
                    // Typing after a discard warning re-arms it: the warning is about
                    // THIS draft, and the draft just changed.
                    setDiscardWarned(false);
                    setEditError(null);
                    setEditAdvisory(null);
                  }}
                  style={fieldStyle}
                />
              </label>
              {/*
                The dialog's POLITE advisory slot — "No changes to save." and the
                unsaved-changes prompt. Neither is an error, so neither may land in
                `ConfirmModal`'s assertive region (which stays reserved for a server
                failure and is rendered by the shell below this body).
              */}
              <div role="status" aria-live="polite">
                {editAdvisory && (
                  <span className="status-pill" data-tone="warn">
                    <span className="status-pill__dot" aria-hidden="true" />
                    {editAdvisory}
                  </span>
                )}
              </div>
            </div>
          }
          onCancel={() => {
            // Story 8.5: Esc, a scrim click and the Cancel button all arrive here, so a
            // dirty draft is not thrown away on the first dismissal — it is announced.
            // (A `ConfirmModal` cannot tell the three apart, which is exactly why the
            // warning covers all of them rather than only the incidental two.)
            // Both sides trimmed, exactly as `onSave` compares them — otherwise a padded
            // stored revision read as dirty the instant the dialog opened, and an
            // administrator who opened a legacy row merely to LOOK at it was warned about
            // unsaved changes they had not made.
            const dirty = editing.draft.trim() !== (editing.entry.device.version ?? '').trim();
            if (dirty && !discardWarned) {
              setDiscardWarned(true);
              // A deliberate-confirmation prompt is not an error — polite region, `warn`
              // tone. The assertive one stays for a server refusal, so a 409/404 already
              // on screen is PRESERVED rather than cleared: dismissing a dialog must not
              // destroy the reason the save failed, which is the one thing that explains
              // why there are unsaved changes at all.
              setEditAdvisory(
                'You have unsaved changes to this revision. Dismiss again to discard them, or press Save to record them.',
              );
              return;
            }
            setEditing(null);
            setEditError(null);
            setEditAdvisory(null);
            setDiscardWarned(false);
          }}
          onConfirm={() => void onSave()}
        />
      )}
    </>
  );
}
